/**
 * Reusable confirmation dialog for operator actions (PR-6b).
 *
 * Used by the callback-inbox re-queue, callback-inbox raw-body reveal,
 * and webhook-outbox retry surfaces. Captures a free-form ``reason``
 * (always required) and, when ``requireTotp`` is true, a 6-digit TOTP
 * code (used by reveal — step-up auth per ADR-0014).
 *
 * The caller owns the submit handler; this component just collects
 * inputs, validates locally, and disables the submit button while the
 * caller's async work is in flight. Surfaces the backend's
 * ``ApiErrorBody.detail`` (or a default message) via the ``error``
 * prop so the parent can render a single inline error string without
 * the dialog reaching into toast / sonner.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ActionConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Submit button label, e.g. "Re-queue" / "Reveal" / "Retry". */
  confirmLabel: string;
  /** Whether to render the step-up TOTP field. */
  requireTotp?: boolean;
  /** Override the button tone (e.g. "destructive" for risky actions). */
  confirmVariant?: "default" | "destructive";
  /** True while the caller's mutation is in flight. */
  pending: boolean;
  /** Inline error string from the backend (or null when none). */
  error: string | null;
  onSubmit: (input: { reason: string; totpCode: string }) => void | Promise<void>;
}

export function ActionConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  requireTotp = false,
  confirmVariant = "default",
  pending,
  error,
  onSubmit,
}: ActionConfirmDialogProps) {
  const [reason, setReason] = useState("");
  const [totpCode, setTotpCode] = useState("");

  // Reset state every time the dialog opens so a stale value from a
  // previous attempt doesn't bleed into a fresh confirmation.
  useEffect(() => {
    if (open) {
      setReason("");
      setTotpCode("");
    }
  }, [open]);

  const canSubmit =
    !pending &&
    reason.trim().length > 0 &&
    (!requireTotp || totpCode.trim().length === 6);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    void onSubmit({ reason: reason.trim(), totpCode: totpCode.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="action-reason">
              Reason
              <span className="ml-1 text-xs text-muted-foreground">(required)</span>
            </Label>
            <Input
              id="action-reason"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. incident #42 — investigating payer X"
              disabled={pending}
            />
            <p className="text-xs text-muted-foreground">
              Captured in the admin audit log for forensics. Be specific.
            </p>
          </div>

          {requireTotp ? (
            <div className="space-y-2">
              <Label htmlFor="action-totp">
                TOTP code
                <span className="ml-1 text-xs text-muted-foreground">(6 digits)</span>
              </Label>
              <Input
                id="action-totp"
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                }
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={pending}
                maxLength={6}
              />
              <p className="text-xs text-muted-foreground">
                Step-up verification — the bytes you're revealing may contain
                PII. The code is single-use; the audit log records every reveal.
              </p>
            </div>
          ) : null}

          {error ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" variant={confirmVariant} disabled={!canSubmit}>
              {pending ? <Loader2 className="animate-spin" /> : null}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
